import React, {
    useMemo,
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
  } from "react";
  import "../SelectAutocomplete/index.scss";
  import { Autocomplete } from "@material-ui/lab";
  import {
    CircularProgress,
    TextField,
    FormControl,
  } from "@material-ui/core";
  import KeyboardArrowDownOutlined from "@material-ui/icons/KeyboardArrowDownOutlined";
  import { strings } from "../../../strings/strings";
  import { AutocompleteProps } from "@material-ui/lab/Autocomplete/Autocomplete";
  import { SearchCityResponse } from "../../../model/city";
  import { Listbox } from "../ListBox";
  
  export interface AutocompleteValue {
    value: string;
    label: string;
  }
  
  export interface ShortcutData extends AutocompleteValue {
    labelShort: string;
  }
  
  interface Props
    extends Partial<
      Omit<AutocompleteProps<AutocompleteValue, boolean, boolean, boolean>, "onChange">
    > {
    name?: string;
    value?: AutocompleteValue;
    placeholder?: string;
    loadFunction?: (query?: string, size?: number) => Promise<SearchCityResponse>;
    charactersToSearch?: number;
    noOptionsText?: string;
    shortcutData?: ShortcutData[];
    disabled?: boolean;
    hidden?: boolean;
    error?: boolean;
    validationError?: string;
    onChange?: (value: any) => void;
    formatResponse?: (obj: AutocompleteValue | string) => any;
    formatValue?: (obj: any) => AutocompleteValue;
    formatOptions?: (obj: any) => AutocompleteValue;
  }
  
  export const SelectInfiniteAutocomplete = (props: Props) => {
    const {
      name,
      value,
      disabled,
      hidden,
      error,
      validationError,
      placeholder,
      loadFunction,
      charactersToSearch = 3,
      noOptionsText,
      shortcutData,
      onChange,
      formatResponse,
      formatValue,
      formatOptions,
      ...rest
    } = props;
  
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<AutocompleteValue[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [size, setSize] = useState(20);
    const [hasMore, setHasMore] = useState(true);
    const searchValueRef = useRef<string>("");
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isTypingSearch, setIsTypingSearch] = useState(false);
    const [inputValue, setInputValue] = useState("");
  
    const listboxRef = useRef<HTMLUListElement | null>(null);
    const scrollPosition = useRef(0);
  
    const setValue = (value: string | AutocompleteValue) => {
      const normalizedValue = value || { value: null, label: null };
      const formattedValue = formatResponse
        ? formatResponse(normalizedValue)
        : normalizedValue;
      onChange && onChange(formattedValue);
    };
  
    const loadData = async (searchValue?: string, reset = false) => {
      if (loading || !hasMore) return;
      try {
        setLoading(true);
        const nextSize = reset ? 20 : size + 20;
        const result = await loadFunction(searchValue, nextSize);
        const response = result.content;
  
        const formattedOptions = formatOptions
          ? response.map(formatOptions)
          : response.map((city: any) => ({
              value: city.id,
              label: city.name,
            }));
  
        setOptions((prev) => {
          if (reset) {
            return formattedOptions;
          }
          return isTypingSearch ? prev : [...prev, ...formattedOptions];
        });
  
        setHasMore(!result.last);
        setSize(nextSize);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    };
  
    const handleChange = (_: any, value: any) => {
      setValue(value);
    };
  
    const handleOpen = () => {
      setOpen(true);
      if (options.length === 0 && !charactersToSearch) {
        loadData();
      }
    };
  
    const handleClose = () => {
      setOpen(false);
      setOptions([]);
      setSize(20);
      setHasMore(true);
      searchValueRef.current = "";
      setInputValue("");
    };
  
    const fieldValue = useMemo(
      () => (formatValue ? formatValue(value) : value),
      [value]
    );
  
    useLayoutEffect(() => {
      if (listboxRef.current) {
        requestAnimationFrame(() => {
          listboxRef.current!.scrollTop = scrollPosition.current;
        });
      }
    }, [options]);
  
    const handleListboxRef = (node: HTMLUListElement) => {
      if (node) {
        listboxRef.current = node;
        node.addEventListener("scroll", handleScroll);
      }
    };
  
    const handleScroll = () => {
      if (!listboxRef.current) return;
      scrollPosition.current = listboxRef.current.scrollTop;
      const { scrollTop, scrollHeight, clientHeight } = listboxRef.current;
      const isBottom = scrollHeight - scrollTop <= clientHeight + 10;
      if (isBottom && hasMore && !loading) {
        loadData(searchValueRef.current);
      }
    };
  
    useEffect(() => {
      return () => {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        if (listboxRef.current) {
          listboxRef.current.removeEventListener("scroll", handleScroll);
        }
      };
    }, []);
  
    return (
      <div className="select-autocomplete">
        <FormControl
          className={`select-one-input__select ${
            open ? "arrow-style-open" : "arrow-style-close"
          }`}
          error={error}
          variant="outlined"
          fullWidth
        >
          <Autocomplete
            {...rest}
            hidden={hidden}
            ListboxComponent={(props) => (
              <Listbox {...props} ref={handleListboxRef} />
            )}
            getOptionSelected={(option, value) =>
              option.value === value.value
            }
            id={name}
            options={options}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            onChange={handleChange}
            getOptionLabel={(option) => option.label || ""}
            loading={loading}
            disabled={disabled}
            value={fieldValue}
            popupIcon={<KeyboardArrowDownOutlined />}
            noOptionsText={noOptionsText || ""}
            title={fieldValue?.label}
            clearText={strings.actions.clear}
            closeText={strings.actions.close}
            openText={strings.actions.open}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
              searchValueRef.current = newInputValue;
  
              scrollPosition.current = 0;
              if (listboxRef.current) {
                listboxRef.current.scrollTop = 0;
              }
  
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
              }
              if (newInputValue.length >= charactersToSearch) {
                setIsTypingSearch(true);
                typingTimeoutRef.current = setTimeout(() => {
                  setSize(20);
                  setHasMore(true);
                  loadData(newInputValue, true).then(() => {
                    setIsTypingSearch(false);
                  });
                }, 300);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={error}
                helperText={error && validationError}
                label={placeholder}
                variant="outlined"
                SelectProps={{
                  MenuProps: { transitionDuration: 0 },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {shortcutData !== undefined && (
            <div className="shortcut-data">
              {shortcutData.map((item) => (
                <span key={item.value} onClick={() => setValue(item)}>
                  {item.labelShort || item.label}
                </span>
              ))}
            </div>
          )}
        </FormControl>
      </div>
    );
  };
  